// Standalone dedupe page — runs the same tenant-wide dedupe endpoint as
// the Dedupe Panel inside a specific portfolio workspace, but is
// reachable at /admin/portfolio-dedupe so you don't have to open a
// portfolio first. Backed by /api/admin/portfolio-dedupe/run.
import { useState, useEffect } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function AdminPortfolioDedupe() {
  const [secret, setSecret] = useState("");
  const [dryRun, setDryRun] = useState(true);
  const [busy, setBusy]     = useState(false);
  const [report, setReport] = useState(null);
  const [healBusy, setHealBusy] = useState(false);
  const [healReport, setHealReport] = useState(null);
  const [woHealBusy, setWoHealBusy] = useState(false);
  const [woHealReport, setWoHealReport] = useState(null);
  const [autoDisabled, setAutoDisabled] = useState(false);
  const [autoBusy, setAutoBusy] = useState(false);

  // Load the tenant's current auto-heal preference on mount.
  useEffect(() => {
    api.get("/admin/heal-wo-unit-labels/status").then(r => {
      setAutoDisabled(!r?.data?.auto_enabled);
    }).catch(() => { /* ignore */ });
  }, []);

  const toggleAuto = async () => {
    if (autoBusy) return;
    setAutoBusy(true);
    try {
      const next = !autoDisabled;   // toggling → new disabled state
      await api.post("/admin/heal-wo-unit-labels/set-auto", { enabled: !next });
      setAutoDisabled(next);
      toast.success(next ? "Auto-heal at login disabled" : "Auto-heal at login enabled");
    } catch {
      toast.error("Couldn't save preference");
    } finally { setAutoBusy(false); }
  };

  const run = async () => {
    if (!secret) { toast.error("Paste the admin secret first"); return; }
    setBusy(true); setReport(null);
    try {
      const r = await api.post("/admin/portfolio-dedupe/run",
        { apply: !dryRun }, { headers: { "X-Admin-Secret": secret } });
      setReport(r.data.report);
      toast.success(dryRun ? "Dry-run complete" : "Dedupe applied");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed — endpoint enabled?"));
    } finally { setBusy(false); }
  };

  const healUnits = async () => {
    if (!window.confirm("Normalize all unit labels tenant-wide? This is safe (idempotent) — it strips 'Unit ', 'Apt ', '#' prefixes so 'Unit A' becomes 'A'. Rerun anytime.")) return;
    setHealBusy(true); setHealReport(null);
    try {
      const r = await api.post("/admin/heal-unit-labels");
      setHealReport(r.data);
      const healed = r.data?.jobs?.healed || 0;
      const healedPc = r.data?.property_configs?.healed || 0;
      if (healed === 0 && healedPc === 0) toast.success("Nothing to heal — labels already clean");
      else toast.success(`Healed ${healed} jobs${healedPc ? ` + ${healedPc} configs` : ""}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Heal failed");
    } finally { setHealBusy(false); }
  };

  const healWoUnits = async () => {
    if (!window.confirm(
      "Clear the unit field on any work order where it accidentally equals the WO number's leading digits (e.g. WO 3607-1 with unit \"3607\")?\n\n" +
      "Only touches WOs where the unit is exactly the same digits (min 3) as the WO number prefix. Safe to rerun — idempotent, scoped to your company."
    )) return;
    setWoHealBusy(true); setWoHealReport(null);
    try {
      const r = await api.post("/admin/heal-wo-unit-labels");
      setWoHealReport(r.data);
      const healed = r.data?.healed || 0;
      if (healed === 0) toast.success("Nothing to heal — no WO/unit collisions found");
      else toast.success(`Cleared ${healed} phantom WO-number unit${healed === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "WO-unit heal failed");
    } finally { setWoHealBusy(false); }
  };

  return (
    <AdminShell>
      <div className="p-4 sm:p-6 max-w-4xl mx-auto" data-testid="portfolio-dedupe-page">
        <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-rose-900 to-slate-950 text-white p-6 sm:p-8 mb-5">
          <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-none tracking-tight">
            Portfolio Cleanup
          </h1>
          <p className="mt-2 text-sm text-white/80 max-w-xl">
            Merge duplicate properties and jobs across your entire company.
            Always dry-run first — the endpoint archives before deleting so
            you can restore later if needed.
          </p>
        </div>

        <div className="border-2 border-teal-500 bg-teal-50 p-4 space-y-3 rounded-xl mb-4" data-testid="heal-units-panel">
          <div className="font-black uppercase text-[11px] tracking-widest text-teal-900">
            Heal Unit Labels — fix phantom "Unit A" / "Unit B"
          </div>
          <p className="text-xs text-teal-900/85">
            Normalizes every job's <code>unit</code> field so <b>"Unit A"</b>, <b>"Apt 2"</b>, and <b>"#3"</b> collapse to <b>"A"</b>, <b>"2"</b>, and <b>"3"</b>. Safe to rerun — idempotent. Scoped to your company only.
          </p>
          <button onClick={healUnits} disabled={healBusy}
            data-testid="heal-units-run"
            className="w-full h-11 bg-teal-600 hover:bg-teal-700 text-white font-black uppercase text-[11px] tracking-widest disabled:opacity-40 rounded">
            {healBusy ? "Healing…" : "Run Unit-Label Heal"}
          </button>
          {healReport && (
            <div className="p-3 border-2 border-teal-500 bg-white text-xs space-y-1 rounded" data-testid="heal-units-report">
              <div className="font-black text-teal-900">
                Jobs scanned: <b>{healReport.jobs?.scanned || 0}</b>{" · "}
                healed: <b>{healReport.jobs?.healed || 0}</b>
              </div>
              <div className="font-black text-teal-900">
                Property configs scanned: <b>{healReport.property_configs?.scanned || 0}</b>{" · "}
                healed: <b>{healReport.property_configs?.healed || 0}</b>
              </div>
              {healReport.sample?.length > 0 && (
                <div className="mt-2 pl-2 border-l-2 border-teal-400 space-y-0.5">
                  <div className="text-[10px] uppercase text-teal-700 font-black">First 20 changes</div>
                  {healReport.sample.map((s, i) => (
                    <div key={i} className="text-[11px]">
                      <span className="font-black">{s.address || "(no address)"}</span> — {" "}
                      <span className="line-through text-slate-500">{s.from || "(empty)"}</span>{" "}
                      → <span className="font-black text-teal-700">{s.to || "(empty)"}</span>
                    </div>
                  ))}
                </div>
              )}
              {(healReport.jobs?.healed === 0 && healReport.property_configs?.healed === 0) && (
                <div className="text-[11px] text-teal-800">Already clean — nothing to change.</div>
              )}
            </div>
          )}
        </div>

        {/* Sibling of Heal Unit Labels — clears "unit" fields that got the
            WO number's leading digits pushed into them by a bad importer. */}
        <div className="border-2 border-amber-500 bg-amber-50 p-4 space-y-3 rounded-xl mb-4" data-testid="wo-unit-heal-panel">
          <div className="font-black uppercase text-[11px] tracking-widest text-amber-900">
            Heal WO/Unit Mix-ups — clear phantom "Unit 3607" tiles
          </div>
          <p className="text-xs text-amber-900/85">
            Clears the <code>unit</code> field on any WO where it accidentally equals
            the WO number's leading digits (e.g. WO <b>3607-1</b> saved with unit <b>"3607"</b>).
            Only touches ≥3-digit collisions so real numbered units like <b>1</b> or <b>2A</b> stay put. Safe to rerun — idempotent, tenant-scoped.
          </p>
          <button onClick={healWoUnits} disabled={woHealBusy}
            data-testid="wo-unit-heal-run"
            className="w-full h-11 bg-amber-600 hover:bg-amber-700 text-white font-black uppercase text-[11px] tracking-widest disabled:opacity-40 rounded">
            {woHealBusy ? "Healing…" : "Run WO/Unit Heal"}
          </button>

          {/* Auto-run-at-login toggle */}
          <label className="flex items-center justify-between gap-3 mt-1 p-2 rounded bg-white/70 border border-amber-300 cursor-pointer"
            data-testid="wo-unit-auto-toggle-row">
            <div className="min-w-0">
              <div className="text-[11px] font-black text-amber-900">Auto-run at admin login</div>
              <div className="text-[10px] text-amber-800/80">
                Silently sweeps once per session on first admin page load. Turn off if you don't want it running unattended.
              </div>
            </div>
            <button onClick={toggleAuto} disabled={autoBusy}
              data-testid="wo-unit-auto-toggle"
              className={`relative flex-shrink-0 h-6 w-11 rounded-full transition-colors ${autoDisabled ? "bg-slate-300" : "bg-amber-600"}`}>
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${autoDisabled ? "left-0.5" : "left-[22px]"}`}/>
            </button>
          </label>
          {woHealReport && (
            <div className="p-3 border-2 border-amber-500 bg-white text-xs space-y-1 rounded" data-testid="wo-unit-heal-report">
              <div className="font-black text-amber-900">
                WOs scanned: <b>{woHealReport.scanned || 0}</b>{" · "}
                cleared: <b>{woHealReport.healed || 0}</b>
              </div>
              {woHealReport.sample?.length > 0 && (
                <div className="mt-2 pl-2 border-l-2 border-amber-400 space-y-0.5">
                  <div className="text-[10px] uppercase text-amber-700 font-black">First 20 changes</div>
                  {woHealReport.sample.map((s, i) => (
                    <div key={i} className="text-[11px]">
                      <span className="font-black">WO {s.wo}</span> · {s.address || "(no address)"} — {" "}
                      <span className="line-through text-slate-500">Unit {s.from}</span>{" "}
                      → <span className="font-black text-amber-700">(cleared)</span>
                    </div>
                  ))}
                </div>
              )}
              {woHealReport.healed === 0 && (
                <div className="text-[11px] text-amber-800">No WO/unit collisions found — nothing to change.</div>
              )}
            </div>
          )}
        </div>

        <div className="border-2 border-rose-500 bg-rose-50 p-4 space-y-3 rounded-xl">
          <div className="font-black uppercase text-[11px] tracking-widest text-rose-900">
            Cleanup — tenant-wide
          </div>
          <input type="password" value={secret} onChange={e => setSecret(e.target.value)}
            data-testid="dedupe-secret"
            placeholder="Same admin secret you use for Bulk Audit"
            className="w-full h-10 px-2 border-2 border-slate-300 text-sm font-mono rounded"/>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)}
              data-testid="dedupe-dryrun"/>
            <span>Dry-run only (uncheck to actually archive duplicates)</span>
          </label>
          <button onClick={run} disabled={busy || !secret}
            data-testid="dedupe-run"
            className="w-full h-11 bg-rose-600 hover:bg-rose-700 text-white font-black uppercase text-[11px] tracking-widest disabled:opacity-40 rounded">
            {busy ? "Running…" : dryRun ? "Preview Duplicates" : "Delete Duplicates"}
          </button>

          {report && (
            <div className="p-3 border-2 border-emerald-400 bg-emerald-50 text-xs space-y-3 rounded" data-testid="dedupe-report">
              <div className="font-black uppercase text-[10px] tracking-widest text-emerald-800">
                {report.apply ? "Applied" : "Dry-run"}
              </div>

              {report.property_configs && (
                <div className="border-t border-emerald-300 pt-2">
                  <div className="font-black text-emerald-900" data-testid="dedupe-pc-summary">
                    Properties (property_configs)
                  </div>
                  <div>Duplicate groups: <b>{report.property_configs.duplicate_groups}</b></div>
                  <div>Duplicates found: <b>{report.property_configs.duplicates_found}</b></div>
                  <div>Archived this run: <b>{report.property_configs.archived}</b></div>
                  {report.property_configs.samples?.length > 0 && (
                    <div className="mt-1 pl-2 border-l-2 border-emerald-400">
                      <div className="text-[10px] uppercase text-emerald-700 font-black">Preview samples</div>
                      {report.property_configs.samples.map((s, i) => (
                        <div key={i} className="mt-1" data-testid={`dedupe-pc-sample-${i}`}>
                          <div><b>{s.address || s.address_key}</b> — {s.count}x</div>
                          <div className="text-[10px] text-slate-600">
                            keep {s.keep_id.slice(-6)} (score {s.richness_kept}) · drop {s.archive_ids.length}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {report.jobs && (
                <div className="border-t border-emerald-300 pt-2">
                  <div className="font-black text-emerald-900" data-testid="dedupe-jobs-summary">
                    Jobs
                  </div>
                  <div>Duplicate groups: <b>{report.jobs.duplicate_groups}</b></div>
                  <div>Duplicates found: <b>{report.jobs.duplicates_found}</b></div>
                  <div>Archived this run: <b>{report.jobs.archived}</b></div>
                  {report.jobs.samples?.length > 0 && (
                    <div className="mt-1 pl-2 border-l-2 border-emerald-400">
                      <div className="text-[10px] uppercase text-emerald-700 font-black">Preview samples</div>
                      {report.jobs.samples.map((s, i) => (
                        <div key={i} className="mt-1" data-testid={`dedupe-job-sample-${i}`}>
                          <div><b>{s.title}</b> — {s.count}x · {s.status}</div>
                          <div className="text-[10px] text-slate-600">
                            @ {s.address_key} · keep {s.keep_id.slice(-6)} · drop {s.archive_ids.length}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="border-t border-emerald-300 pt-2">
                <div>Legacy "merged"-status pairs: <b>{report.merged_pairs_found}</b> found · <b>{report.merged_deleted}</b> deleted</div>
                <div>"Unit Unit" prefix fixes: <b>{report.unit_prefix_fixed}</b></div>
                {report.unit_prefix_samples?.length > 0 && (
                  <ul className="mt-1 pl-4 list-disc text-[10px] text-slate-600">
                    {report.unit_prefix_samples.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
}
